import { delay, put, select, takeLatest } from 'typed-redux-saga/macro';

import {
  KEYWORD_MATCHES_READY,
  KEYWORD_SEARCH,
  SET_KEYWORD_SEARCH_OPTIONS,
  SET_PAGE_TEXT,
  SET_PAGE_TEXT_BATCH,
} from '../actions';
import { pageKeywordMatches } from '../helpers';
import { selectSession } from '../selectors';
import { KEYWORD_SEARCH_DEBOUNCE_MS } from '../store';
import { KeywordMatch } from '../models';

function* runKeywordSearch(phrase: string) {
  const session = yield* select(selectSession);
  if (!session) {
    return;
  }

  // Normalize once so KEYWORD_MATCHES_READY stamps the trimmed phrase
  // into state — without this, a whitespace-only input (or one with
  // leading/trailing spaces) would round-trip back as the persisted
  // value, breaking strict-equality consumers that decide whether a
  // search is active.
  const normalizedPhrase = phrase.trim();
  if (!normalizedPhrase) {
    yield* put(
      KEYWORD_MATCHES_READY({ phrase: normalizedPhrase, matches: [] })
    );
    return;
  }

  const pageTexts: Record<number, string> = session.pageTexts ?? {};
  const pageTextItems = session.pageTextItems ?? {};
  const matches: KeywordMatch[] = [];
  const { matchCase, wholeWord } = session.keywordSearch;

  // Iterate the pages that have actually delivered their text rather
  // than 1..session.totalPages. `session.totalPages` is only a seed
  // (pdfjs's real count isn't written back to session state), so looping
  // by it would cap the search short on longer PDFs. `pageTexts` is the
  // ground truth
  // of "pages we've extracted text for"; sorting the numeric keys
  // keeps results in document order so prev/next nav still walks
  // matches top-to-bottom.
  const pagesWithText = Object.keys(pageTexts)
    .map((p) => Number(p))
    .filter((p) => Number.isFinite(p) && p > 0)
    .sort((a, b) => a - b);
  for (const page of pagesWithText) {
    for (const m of pageKeywordMatches(
      page,
      pageTexts[page] ?? '',
      pageTextItems[page] ?? [],
      normalizedPhrase,
      { matchCase, wholeWord }
    )) {
      matches.push(m);
    }
  }

  yield* put(KEYWORD_MATCHES_READY({ phrase: normalizedPhrase, matches }));
}

export function* keywordSearchSaga() {
  // takeLatest debounces both flows: a new KEYWORD_SEARCH cancels any in-
  // flight iteration, and toggling matchCase / wholeWord while typing
  // doesn't pile up parallel searches either. The debounce only gates
  // the typing path; option toggles re-run immediately.
  yield* takeLatest(KEYWORD_SEARCH, function* ({ payload: { phrase } }) {
    yield* delay(KEYWORD_SEARCH_DEBOUNCE_MS);
    yield* runKeywordSearch(phrase);
  });
  yield* takeLatest(SET_KEYWORD_SEARCH_OPTIONS, function* () {
    const session = yield* select(selectSession);
    if (!session) {
      return;
    }
    yield* runKeywordSearch(session.keywordSearch.phrase);
  });
  // Re-run the active search whenever a new page's text becomes
  // available. react-pdf lazy-renders pages so on first open only the
  // top of the document has its text in `pageTexts`; the saga's loop
  // would silently skip the rest and the user would see an under-count
  // (typically "matches on the first 3 pages only"). takeLatest +
  // delay coalesces the burst of SET_PAGE_TEXT events while pages
  // populate — one re-run after the burst settles, no per-page thrash.
  yield* takeLatest(SET_PAGE_TEXT, function* () {
    yield* delay(150);
    const session = yield* select(selectSession);
    if (!session || !session.keywordSearch.phrase.trim()) {
      return;
    }
    yield* runKeywordSearch(session.keywordSearch.phrase);
  });
  // The pre-pass (VE-25293) delivers every page's text in one
  // SET_PAGE_TEXT_BATCH instead of a burst of SET_PAGE_TEXT, so re-run the
  // active search once it lands — otherwise a search typed before the pre-pass
  // completes would keep its partial (render-path-only) result.
  yield* takeLatest(SET_PAGE_TEXT_BATCH, function* () {
    yield* delay(150);
    const session = yield* select(selectSession);
    if (!session || !session.keywordSearch.phrase.trim()) {
      return;
    }
    yield* runKeywordSearch(session.keywordSearch.phrase);
  });
}

// NOTE: keyword redactions (single "Redact" + "Redact all") are created in
// DocumentEditor from `keywordRedactionsFromItems`, NOT by this saga. It and
// `pageKeywordMatches` both use `rowBoxesForSpan`, so a highlight row and its
// redaction box cover the same glyphs; the highlight only adds a pad. The
// former KEYWORD_REDACT_ONE saga (and its item-space buildKeywordRedaction)
// was dead — its action was never dispatched — and has been removed.
